/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Accordion,
  AccordionHeader,
  AccordionPanel,
  Button,
  Dialog,
  Divider,
  Radio,
  RadioGroup,
  Typography2,
  breakpoints,
} from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import CheckableExpandableSearchFilterGroup from 'bundles/search-common/components/experiments/filters2024/CheckableExpandableSearchFilterGroup';
import { CourseraPlusFilterChipButton } from 'bundles/search-common/components/experiments/filters2024/components/CourseraPlusFilterChipButton';
import {
  BINARY_FILTER_KEYS,
  getCustomFilterGroupLabel,
  getFilterLabelsForGroup,
  getSingleFilterValueForKey,
} from 'bundles/search-common/components/experiments/filters2024/utils';
import { useSearchFilters } from 'bundles/search-common/components/filters/useSearchFilters';
import { DEFAULT_SORTED_VALUE, NEW_SORTED_VALUE, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { DEFAULT_SEARCH_FILTERS_ALL_FILTERS_PANEL } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchPageEventingData, SearchSortType } from 'bundles/search-common/types';
import { getAutoApplyFilters } from 'bundles/search-common/utils/experimentUtils';
import { scrollUpIfNecessary } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  eventingData: SearchPageEventingData;
  initialExpandedGroup?: string;
  facets?: string[];
};

type PanelState = {
  selectedSortBy: SearchSortType;
  expandedSections: Record<string, boolean>;
  collapseCallbacks: Record<string, (() => void) | null>;
  stableHitsTotalCount: number | undefined;
};

const styles = {
  panelContainer: css`
    .cds-Dialog-dialog {
      max-width: 360px;
      width: 360px;
      height: 100vh;
      min-height: 100vh;
      position: fixed;
      right: 0;
      top: 0;
      left: auto;
      border-radius: 0;
      transform: none;

      ${breakpoints.down('sm')} {
        max-width: 100%;
        width: 100%;
        left: 0;
        right: 0;
      }
    }

    /* Position close button with 24px top/bottom and 16px from right */
    .cds-iconButton-ghost {
      position: absolute;
      top: var(--cds-spacing-200);
      right: var(--cds-spacing-200);
      bottom: auto;
      left: auto;
      color: var(--cds-color-grey-975);

      ${breakpoints.down('sm')} {
        top: var(--cds-spacing-50);
      }
    }
  `,
  dialogTitle: css`
    position: absolute;
    top: var(--cds-spacing-300);
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;

    ${breakpoints.down('sm')} {
      top: 10px;
    }
  `,
  divider: css`
    position: absolute;
    top: 72px;
    left: 0;
    right: 0;

    ${breakpoints.down('sm')} {
      top: 44px;
    }
  `,
  content: css`
    position: absolute;
    top: 72px; /* Below title and divider */
    bottom: 104px; /* Above footer including divider */
    left: 0;
    right: 0;
    padding: 0;
    overflow-y: auto;

    ${breakpoints.down('sm')} {
      top: 44px;
      bottom: 76px;
    }
  `,
  accordionHeaderButton: css`
    .cds-AccordionHeader-button {
      padding: var(--cds-spacing-150) var(--cds-spacing-200) var(--cds-spacing-150) var(--cds-spacing-200);
      margin: 0;
    }

    .cds-AccordionHeader-content {
      margin: 0;
    }
  `,
  accordionHeaderLabel: css`
    .cds-AccordionHeader-labelGroup {
      span {
        font-weight: 400;
        font-family: var(--cds-typography-body-primary);
        line-height: 1.5rem;
        letter-spacing: var(--cds-letter-spacing-0);
      }
    }
  `,
  accordionHeaderExpanded: css`
    .cds-AccordionHeader-labelGroup {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      gap: var(--cds-spacing-50);
      flex-wrap: wrap;
      min-height: 1.5rem; /* Ensure consistent height */

      span:first-child {
        font-weight: 600;
        display: flex;
        align-items: center;
      }

      span:nth-child(2) {
        margin-top: 0;
      }
    }
  `,
  accordionHeaderCollapsed: css`
    .cds-AccordionHeader-labelGroup {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      gap: var(--cds-spacing-50);
      flex-wrap: wrap;
      min-height: 1.5rem; /* Ensure consistent height */

      span:first-child {
        font-weight: 400;
        font-family: var(--cds-typography-body-primary);
        line-height: 1.5rem;
        letter-spacing: var(--cds-letter-spacing-0);
        display: flex;
        align-items: center;
      }

      span:nth-child(2) {
        margin-top: 0;
      }
    }
  `,

  binaryFiltersSection: css`
    padding: var(--cds-spacing-300) var(--cds-spacing-200) var(--cds-spacing-150);
    display: flex;
    overflow-x: auto;
    gap: var(--cds-spacing-100);
  `,
  sortBySection: css`
    margin-top: var(--cds-spacing-150);
  `,
  footer: css`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    background-color: var(--cds-color-white-0);
  `,
  footerButtons: css`
    display: flex;
    gap: var(--cds-spacing-100);
    padding: var(--cds-spacing-100) var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200);

    ${breakpoints.up('sm')} {
      flex-direction: column;
    }

    ${breakpoints.down('sm')} {
      flex-direction: row;
      padding-bottom: var(--cds-spacing-400);
    }
  `,
  radioButton: css`
    .cds-checkboxAndRadio-label {
      padding: var(--cds-spacing-50) 0;
      margin: 0;
    }
  `,
};

const AllSearchFiltersPanel = (props: Props) => {
  const { isOpen, onClose, eventingData, initialExpandedGroup, facets } = props;
  const { filterGroupConfigs, filterKeyToOptionsMap, facetFilters } = useSearchFilters({
    facetOrder: facets && facets.length > 0 ? facets : DEFAULT_SEARCH_FILTERS_ALL_FILTERS_PANEL,
    useStableResults: true,
  });
  const { getSearchResults, setSortBy, applySearchStateUpdates, isLoading, setFiltersByFacet } = useSearch();
  const { sortBy, hitsTotalCount } = getSearchResults(SEARCH_MAIN_INDEX_ID) || {};
  const queryAutoApplyFilters = getAutoApplyFilters();

  const [panelState, setPanelState] = useState<PanelState>(() => {
    const initialExpandedSections: Record<string, boolean> = { sortBy: !initialExpandedGroup }; // Collapse sort if a filter group is specified

    filterGroupConfigs.forEach((group) => {
      initialExpandedSections[group.property] = group.property === initialExpandedGroup;
    });

    return {
      selectedSortBy: sortBy || DEFAULT_SORTED_VALUE,
      expandedSections: initialExpandedSections,
      collapseCallbacks: {},
      stableHitsTotalCount: hitsTotalCount,
    };
  });

  const hasInitializedRef = useRef(false);
  const filterGroupConfigsRef = useRef(filterGroupConfigs);

  useEffect(() => {
    filterGroupConfigsRef.current = filterGroupConfigs;
  }, [filterGroupConfigs]);

  useEffect(() => {
    if (isOpen && initialExpandedGroup && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setPanelState((prev) => {
        const newExpandedSections: Record<string, boolean> = {
          sortBy: false, // Collapse sort when opening with a specific group
        };

        filterGroupConfigsRef.current.forEach((group) => {
          newExpandedSections[group.property] = group.property === initialExpandedGroup;
        });

        return {
          ...prev,
          expandedSections: newExpandedSections,
        };
      });
    }

    if (!isOpen) {
      hasInitializedRef.current = false;
      setPanelState((prev) => {
        const initialExpandedSections: Record<string, boolean> = { sortBy: !initialExpandedGroup };

        filterGroupConfigsRef.current.forEach((group) => {
          initialExpandedSections[group.property] = group.property === initialExpandedGroup;
        });

        return {
          ...prev,
          expandedSections: initialExpandedSections,
        };
      });
    }
  }, [isOpen, initialExpandedGroup]);

  const track = useTracker();

  const hasFiltersApplied = useMemo(
    () =>
      (facetFilters && facetFilters.length > 0 && facetFilters.some((filterGroup) => filterGroup.length > 0)) ||
      sortBy !== DEFAULT_SORTED_VALUE,
    [facetFilters, sortBy]
  );

  useEffect(() => {
    if (!isLoading) {
      setPanelState((prev) => ({
        ...prev,
        stableHitsTotalCount: !hasFiltersApplied ? undefined : hitsTotalCount,
      }));
    }
  }, [isLoading, hitsTotalCount, hasFiltersApplied]);

  const handleSortByChange = useCallback<(sortValue: SearchSortType) => void>(
    (sortValue: SearchSortType) => {
      setPanelState((prev) => ({
        ...prev,
        selectedSortBy: sortValue,
      }));
      setSortBy(SEARCH_MAIN_INDEX_ID, sortValue);
      track('apply_filter', {
        filter: [`sortBy:${sortValue}`],
        filterSource: 'unified_modal',
      });
    },
    [setSortBy, track]
  );

  const handleAccordionChange = useCallback<
    (sectionKey: string) => (event: React.ChangeEvent<unknown>, expanded: boolean) => void
  >(
    (sectionKey: string) => (event: React.ChangeEvent<unknown>, expanded: boolean) => {
      setPanelState((prev) => {
        if (sectionKey === 'sortBy') {
          return {
            ...prev,
            expandedSections: {
              ...prev.expandedSections,
              sortBy: expanded,
            },
          };
        }

        const newExpandedSections: Record<string, boolean> = {
          sortBy: prev.expandedSections.sortBy || false, // Preserve sortBy state
        };

        filterGroupConfigs.forEach((group) => {
          const shouldBeExpanded = group.property === sectionKey ? expanded : false;
          newExpandedSections[group.property] = shouldBeExpanded;

          if (!shouldBeExpanded && prev.expandedSections[group.property] && prev.collapseCallbacks[group.property]) {
            prev.collapseCallbacks[group.property]?.();
          }
        });

        return {
          ...prev,
          expandedSections: newExpandedSections,
        };
      });
    },
    [filterGroupConfigs]
  );

  const handleClearAll = useCallback<() => void>(() => {
    applySearchStateUpdates(SEARCH_MAIN_INDEX_ID, {
      clearFilters: true,
      sortBy: DEFAULT_SORTED_VALUE,
      clearAutoAppliedFilters: queryAutoApplyFilters,
    });
    track('apply_filter', {
      filter: [],
      filterSource: 'unified_modal',
      isReset: true,
    });

    setPanelState((prev) => ({
      ...prev,
      selectedSortBy: DEFAULT_SORTED_VALUE,
    }));
  }, [applySearchStateUpdates, track, queryAutoApplyFilters]);

  const handleApply = useCallback<() => void>(() => {
    // Sort by changes are applied automatically, so just close the panel
    onClose();
    scrollUpIfNecessary({ mustBeScrolledPastThisElementId: 'search-results-header-wrapper' });
  }, [onClose]);

  const handleClose = useCallback<() => void>(() => {
    onClose();
    setPanelState((prev) => ({
      ...prev,
      selectedSortBy: sortBy || DEFAULT_SORTED_VALUE,
    }));
  }, [onClose, sortBy]);

  const handleCollapseCallback = useCallback<(groupProperty: string) => (collapseShowMore: () => void) => void>(
    (groupProperty: string) => {
      return (collapseShowMore: () => void) => {
        setPanelState((prev) => ({
          ...prev,
          collapseCallbacks: {
            ...prev.collapseCallbacks,
            [groupProperty]: collapseShowMore,
          },
        }));
      };
    },
    []
  );

  const getFilterLabelsForGroupMemo = useCallback<(groupProperty: string) => string[]>(
    (groupProperty: string): string[] => getFilterLabelsForGroup(groupProperty, facetFilters, filterKeyToOptionsMap),
    [facetFilters, filterKeyToOptionsMap]
  );

  // Split filter groups into binary and non-binary
  const binaryFilterGroups = filterGroupConfigs.filter((group) => BINARY_FILTER_KEYS.includes(group.property));
  const nonBinaryFilterGroups = filterGroupConfigs.filter((group) => !BINARY_FILTER_KEYS.includes(group.property));
  return (
    <Dialog css={styles.panelContainer} open={isOpen} onClose={handleClose} aria-labelledby="dialogHeading">
      <Typography2 variant="subtitleLarge" component="h3" css={styles.dialogTitle} id="dialogHeading">
        {_t('Filter & Sort')}
      </Typography2>
      <Divider css={styles.divider} />

      <div css={styles.content}>
        {/* Binary Filter Chips */}
        {binaryFilterGroups.length > 0 && (
          <div css={styles.binaryFiltersSection}>
            {binaryFilterGroups.map((group) => {
              const filterGroupKey = group.property;

              if (filterGroupKey === 'isPartOfCourseraPlus') {
                const isCPlusActive = getSingleFilterValueForKey(filterGroupKey, facetFilters) === 'true';
                return (
                  <CourseraPlusFilterChipButton
                    key={filterGroupKey}
                    isActive={isCPlusActive}
                    onClick={() => {
                      setFiltersByFacet(
                        SEARCH_MAIN_INDEX_ID,
                        filterGroupKey,
                        isCPlusActive ? [] : [`${filterGroupKey}:true`]
                      );
                    }}
                  />
                );
              }

              return null;
            })}
          </div>
        )}

        {/* Sort By Section */}
        <div css={binaryFilterGroups.length === 0 ? styles.sortBySection : undefined}>
          <Accordion
            key="sortBy"
            variant="silent"
            expanded={panelState.expandedSections.sortBy}
            onChange={handleAccordionChange('sortBy')}
          >
            <AccordionHeader
              label={_t('Sort by')}
              supportText={(() => {
                if (panelState.expandedSections.sortBy) return undefined;
                return panelState.selectedSortBy === NEW_SORTED_VALUE ? _t('Newest') : _t('Best Match');
              })()}
              indicatorAlignment="end"
              css={[
                styles.accordionHeaderButton,
                panelState.expandedSections.sortBy ? styles.accordionHeaderExpanded : styles.accordionHeaderCollapsed,
              ]}
            />
            <AccordionPanel>
              <RadioGroup
                css={styles.radioButton}
                value={panelState.selectedSortBy}
                onChange={(e) => handleSortByChange(e.target.value as SearchSortType)}
                aria-label={_t('Sort by')}
              >
                <Radio value={DEFAULT_SORTED_VALUE} label={_t('Best Match')} />
                <Radio value={NEW_SORTED_VALUE} label={_t('Newest')} />
              </RadioGroup>
            </AccordionPanel>
          </Accordion>
        </div>

        {/* Filter Sections */}
        {nonBinaryFilterGroups
          .filter((group) => !!filterKeyToOptionsMap[group.property]?.length)
          .map((group) => (
            <div key={group.property}>
              <Accordion
                key={group.property}
                variant="silent"
                expanded={panelState.expandedSections[group.property]}
                onChange={handleAccordionChange(group.property)}
              >
                <AccordionHeader
                  label={getCustomFilterGroupLabel()[group.property] || group.getName()}
                  supportText={(() => {
                    const appliedLabels = getFilterLabelsForGroupMemo(group.property);
                    const isCollapsed = !panelState.expandedSections[group.property];
                    return isCollapsed && appliedLabels.length > 0 ? appliedLabels.join(', ') : undefined;
                  })()}
                  indicatorAlignment="end"
                  css={[
                    styles.accordionHeaderButton,
                    panelState.expandedSections[group.property]
                      ? styles.accordionHeaderExpanded
                      : (() => {
                          const appliedLabels = getFilterLabelsForGroupMemo(group.property);
                          const hasSupport = appliedLabels.length > 0;
                          return hasSupport ? styles.accordionHeaderCollapsed : styles.accordionHeaderLabel;
                        })(),
                  ]}
                />
                <AccordionPanel>
                  <CheckableExpandableSearchFilterGroup
                    groupConfig={group}
                    groupValues={filterKeyToOptionsMap[group.property] || []}
                    eventingData={{
                      searchIndex: eventingData.searchIndex,
                      searchIndexPosition: eventingData.searchIndexPosition,
                    }}
                    shouldHideGroupLabel={true}
                    shouldAutoApply={true}
                    onCollapse={handleCollapseCallback(group.property)}
                    isAllFiltersPanel={true}
                  />
                </AccordionPanel>
              </Accordion>
            </div>
          ))}
      </div>

      <div css={styles.footer}>
        <Divider />
        <div css={styles.footerButtons}>
          <Button fullWidth variant="primary" size="small" onClick={handleApply}>
            {hasFiltersApplied && panelState.stableHitsTotalCount
              ? _t('View (#{hitsTotalCount})', { hitsTotalCount: panelState.stableHitsTotalCount })
              : _t('View')}
          </Button>
          <Button fullWidth variant="secondary" size="small" onClick={handleClearAll} disabled={!hasFiltersApplied}>
            {_t('Clear all')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default AllSearchFiltersPanel;
